/**
 * 3D Interactive Lunar Surface Markers
 * Renders glowing interactive pins on the lunar globe for Apollo sites,
 * robotic landers, impact craters, and maria.
 */

import * as THREE from 'three';
import { latLonToVector3 } from '../utils/math.js';
import { LUNAR_FEATURES } from '../data/lunarFeatures.js';

export class MarkersManager {
  constructor(scene, camera, sphereRadius = 5.0) {
    this.scene = scene;
    this.camera = camera;
    this.radius = sphereRadius;
    this.markersGroup = new THREE.Group();
    this.markersGroup.name = 'LunarMarkers';
    this.scene.add(this.markersGroup);

    this.markers = [];
    this.raycaster = new THREE.Raycaster();
    this.hoveredMarker = null;
    this.filterType = 'all'; // 'all', 'apollo', 'robotic', 'crater', 'mare'

    this.initMarkers();
  }

  initMarkers() {
    const pinGeom = new THREE.SphereGeometry(0.04, 16, 16);
    const ringGeom = new THREE.RingGeometry(0.05, 0.08, 24);

    LUNAR_FEATURES.forEach(feature => {
      const pos = latLonToVector3(feature.lat, feature.lon, this.radius * 1.008);
      const markerRoot = new THREE.Group();
      markerRoot.position.set(pos.x, pos.y, pos.z);
      markerRoot.lookAt(0, 0, 0); // Orient outward from center

      let color = 0x00f3ff;
      if (feature.type === 'apollo') color = 0xffd166;
      else if (feature.type === 'crater') color = 0xef476f;
      else if (feature.type === 'mare') color = 0x06d6a0;
      else if (feature.type === 'farside') color = 0xb5179e;

      const pinMat = new THREE.MeshBasicMaterial({ color });
      const pinMesh = new THREE.Mesh(pinGeom, pinMat);

      // Pulse ring
      const ringMat = new THREE.MeshBasicMaterial({
        color,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8
      });
      const ringMesh = new THREE.Mesh(ringGeom, ringMat);
      ringMesh.position.z = 0.01;

      markerRoot.add(pinMesh);
      markerRoot.add(ringMesh);

      // Store reference
      markerRoot.userData = { feature, ringMesh, baseScale: 1.0 };
      this.markersGroup.add(markerRoot);
      this.markers.push(markerRoot);
    });
  }

  update(time) {
    // Animate pulse rings
    const pulse = 1.0 + Math.sin(time * 3) * 0.25;
    const opacity = 0.5 + Math.cos(time * 3) * 0.3;

    this.markers.forEach(m => {
      if (m.visible) {
        m.userData.ringMesh.scale.set(pulse, pulse, 1);
        m.userData.ringMesh.material.opacity = opacity;
      }
    });
  }

  checkIntersection(normalizedMouse) {
    this.raycaster.setFromCamera(normalizedMouse, this.camera);
    const visibleMarkers = this.markers.filter(m => m.visible);
    const intersects = this.raycaster.intersectObjects(visibleMarkers, true);

    if (intersects.length > 0) {
      // Find top group
      let obj = intersects[0].object;
      while (obj.parent && obj.parent !== this.markersGroup) {
        obj = obj.parent;
      }
      return obj.userData.feature || null;
    }
    return null;
  }

  setFilter(type) {
    this.filterType = type;
    this.markers.forEach(m => {
      if (type === 'all') {
        m.visible = true;
      } else {
        m.visible = m.userData.feature.type === type;
      }
    });
  }

  setVisible(visible) {
    this.markersGroup.visible = visible;
  }
}
